import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SpinLoading } from 'antd-mobile';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ErrorProvider } from "./contexts/ErrorContext";

// Components
import ProtectedRoute from "./components/ProtectedRoute";
import NativeAuthComplete from "./components/NativeAuthComplete";


// Store
import store from './store/store';
import UseAuth from "./hooks/UseAuth";

// Pages
const Login = lazy(() => import("./pages/Login"));
const Home = lazy(() => import("./pages/Home"));
const SignUp = lazy(() => import("./pages/SignUp"));
const SignUpGoogle = lazy(() => import("./pages/SignUp/SignUpGoogle"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const LoginAdmin = lazy(() => import("./pages/Login/LoginAdmin"));
const HomeAdmin = lazy(() => import("./pages/HomeAdmin"));
const NoAccess = lazy(() => import("./pages/NoAccess"));
const NotFound = lazy(() => import("./NotFound"));
const UpsertIssue = lazy(() => import("./pages/UpsertIssue"));
const UpsertIssueV2 = lazy(() => import("./pages/UpsertIssueV2")); // V2
const EditIssue = lazy(() => import("./pages/UpsertIssue/Edit"));
const EditIssueV2 = lazy(() => import("./pages/UpsertIssueV2/Edit")); // V2
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const ProjectDetailV2 = lazy(() => import("./pages/ProjectDetailV2")); // V2
const RecordIssue = lazy(() => import("./pages/RecordIssue"));
const RecordIssueV2 = lazy(() => import("./pages/RecordIssueV2")); // V2
const ProjectDetailAdmin = lazy(() => import("./pages/ProjectDetail/ProjectDetailAdmin"));
const ProjectDetailAdminV2 = lazy(() => import("./pages/ProjectDetailV2/ProjectDetailAdmin")); // V2
const EditIssueAdmin = lazy(() => import("./pages/UpsertIssue/EditAdmin"));
const EditIssueAdminV2 = lazy(() => import("./pages/UpsertIssueV2/EditAdmin")); // V2
const ManageClient = lazy(() => import("./pages/ManageClient"));
const ManageUser = lazy(() => import("./pages/ManageUser"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const AudioResponse = lazy(() => import("./pages/AudioResponses"));

const RedirectHomeUrl = () => {
  const { getToken, isTokenRefreshing } = useAuth();

  useEffect(() => {
    const preRedirect = async () => {
      const token = await getToken();
      console.log('🔑🔑Token:🔑🔑', token);
      if (token) {
      window.location.href = '/home';
      } else {
        window.location.href = '/login';
      }
    }

    if (!isTokenRefreshing) {
      preRedirect();
    }
  }, [getToken, isTokenRefreshing]);

  return null;
}

const Main = () => {
  const queryClient = new QueryClient();

  return (
    <Provider store={store}>
      <ErrorProvider>
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <MainContent />
          </QueryClientProvider>
        </AuthProvider>
      </ErrorProvider>
    </Provider>
  );
};

const MainContent = () => {
  const { userRoles } = UseAuth();
  const isSuperAdmin = userRoles.find(role => role.name === 'super admin');
  const isV2 = localStorage.getItem("isV2") === "true";

  if (isV2 && !isSuperAdmin) {
    return (
      <div>
        <h1>You are not authorized to access v2 pages</h1>
        <button type="button" onClick={() => {
          localStorage.setItem("isV2", "false");
          window.location.href = '/home';
        }}>
          Go to V1
        </button>
      </div>
    )
  }

  return (
    <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><SpinLoading /></div>}>
      {isV2 && (
        <div style={{ position: 'fixed', top: 0, left: 0, backgroundColor: '#ff5657', padding: '4px 12px', zIndex: 1000, color: 'white', borderBottomRightRadius: 18 }}>
          <h6 style={{ margin: 0, fontSize: 12 }}>V2</h6>
        </div>
      )}
      <Routes>
        <Route
          path="/issue"
          element={
            <ProtectedRoute 
              requirePermission={["permission:page:view-issue-detail-page"]}
              element={isV2 ? <UpsertIssueV2 /> : <UpsertIssue />} 
            />
          }
        />
        <Route
          path="/issue/:id"
          element={
            <ProtectedRoute 
              requirePermission={["permission:page:view-issue-edit-page"]}
              element={isV2 ? <EditIssueV2 /> : <EditIssue />} 
            />
          }
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute 
              requirePermission={["permission:page:view-project-page"]}
              element={<Home />} 
            />
          }
        />
        <Route
          path="/issue/list/:id"
          element={
            <ProtectedRoute 
              requirePermission={["permission:page:view-issue-page"]}
              element={isV2 ? <ProjectDetailV2 /> : <ProjectDetail />} 
            />
          }
        />
        <Route 
          path="/record-issue/:loggingType"
          element={
            <ProtectedRoute 
              requirePermission={["permission:page:view-record-page"]}
              element={isV2 ? <RecordIssueV2 /> : <RecordIssue />} 
            />
          } 
        />
        <Route 
          path="/home-admin" 
          element={
            <ProtectedRoute 
              requirePermission={["permission:page:view-admin-project-page"]}
              element={<HomeAdmin />} 
              context={'admin'}
            />
          } 
        />
        <Route path="/user-admin" element={
           <ProtectedRoute 
            requirePermission={["permission:page:view-admin-user-page"]}
            element={<ManageUser />} 
            context={'admin'}
         />
        } />
        <Route path="/client-admin" element={
          <ProtectedRoute 
            requirePermission={["permission:page:view-admin-client-page"]}
            element={<ManageClient />} 
            context={'admin'}
          />
        } />
        <Route
          path="/issue-admin/list/:id"
          element={
            <ProtectedRoute 
              requirePermission={["permission:page:view-admin-issue-page"]}
              element={isV2 ? <ProjectDetailAdminV2 /> : <ProjectDetailAdmin />}
              context={'admin'}
            />
          }
        />
        <Route
          path="/issue-admin/:id"
          element={
            <ProtectedRoute 
              requirePermission={["permission:page:view-issue-edit-page"]}
              element={isV2 ? <EditIssueAdminV2 /> : <EditIssueAdmin />}
              context={'admin'}
            />
          }
        />

        <Route
          path="/delete-account"
          element={
            <ProtectedRoute 
              requirePermission={["permission:page:view-project-page"]}
              element={<DeleteAccount />} 
            />
          }
        />

        <Route path="/audio-response" element={
          <ProtectedRoute 
            requirePermission={[
              "permission:page:view-admin-audio-response",
              "permission:audio-response-storage:get-all",
              "permission:audio-response-storage:get-audio-by-id",
              "permission:audio-response-storage:get-by-id"
            ]}
            element={<AudioResponse />} 
          />
        } />

        <Route path="/auth/native/complete" element={<NativeAuthComplete />} />
        <Route path="/login-admin" element={<LoginAdmin />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/signup-google" element={<SignUpGoogle />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/no-access" element={<NoAccess />} />
        <Route path="/" element={<RedirectHomeUrl />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default Main;